import React, { useEffect, useMemo, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { ArrowLeft, CheckCircle2, AlertTriangle, Save, Plus, Camera, MapPin, ScanLine } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { fmtDateTime, fmtNum } from "../lib/format";
import { useConfirm } from "../components/ConfirmDialog";
import { toast } from "sonner";
import CameraScanner from "../components/BarcodeScanner";
import { unmojibakeWithDiagnostic } from "../lib/scan-unmojibake";
import { parseQrLocally } from "../lib/scan-local-resolver";

export default function InventoryDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [inv, setInv] = useState(null);
  const [products, setProducts] = useState([]);
  const [distributor, setDistributor] = useState(null);
  // Phase 91 — Clé unifiée : lot_id en mode BY_LOT, product_id en mode BY_PRODUCT
  const [drafts, setDrafts] = useState({});
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({ product_id: "", counted_quantity: "", lot_number: "", dlc: "", emplacement_number: "" });
  const [addSaving, setAddSaving] = useState(false);
  // Phase 87aq — Scan caméra produit
  const [scanOpen, setScanOpen] = useState(false);
  const [scanError, setScanError] = useState(null);
  // Phase 87bd — Désambiguïsation EAN13 quand plusieurs produits matchent
  const [ambiguousOpen, setAmbiguousOpen] = useState(false);
  const [ambiguousCandidates, setAmbiguousCandidates] = useState([]);
  const [ambiguousCode, setAmbiguousCode] = useState("");
  // Phase 87bd — État de la résolution backend (spinner pendant /scan/resolve)
  const [resolving, setResolving] = useState(false);
  // Phase 91 — Map lot_number → product_id, préchargée depuis tous les lots du dépôt.
  // Permet le scan QR Maison Thaï en mode BY_PRODUCT (le QR contient un lot précis,
  // mais on doit focuser la ligne produit agrégée).
  const [lotToProductMap, setLotToProductMap] = useState(new Map());
  // Phase 87ar — Scan HID (lecteur Bluetooth/USB type Inateck BCST-47).
  // Champ visible en haut du tableau qui capte les keystrokes du lecteur + Enter.
  const [hidScanInput, setHidScanInput] = useState("");
  const hidScanFieldRef = useRef(null);
  // Auto-focus du champ scan HID : reprend le focus toutes les 600ms SAUF
  // si l'opérateur est en train de taper dans un autre champ (count input,
  // recherche, etc.) — pour ne pas voler le focus pendant la saisie manuelle.
  useEffect(() => {
    const interval = setInterval(() => {
      const active = document.activeElement;
      if (!hidScanFieldRef.current) return;
      if (active === hidScanFieldRef.current) return;
      // Ne vole pas le focus si un autre input/select/textarea est actif
      if (active && ["INPUT", "SELECT", "TEXTAREA"].includes(active.tagName)) return;
      // Ne vole pas le focus si une modale est ouverte (caméra, ajout produit)
      if (scanOpen || addOpen) return;
      hidScanFieldRef.current.focus();
    }, 600);
    return () => clearInterval(interval);
  }, [scanOpen, addOpen]);
  const inputRefs = useRef({}); // clé unifiée (lot_id ou product_id) -> input ref
  const confirm = useConfirm();

  const load = async () => {
    const { data } = await api.get(`/inventories/${id}`);
    setInv(data);
    const ps = await api.get("/products");
    setProducts(ps.data);
    const { data: dist } = await api.get("/distributors");
    setDistributor(dist.find((d) => d.id === data.distributor_id) || null);

    // Phase 91 — Si mode BY_PRODUCT, on précharge tous les lots du dépôt pour
    // résoudre le scan QR (qui contient un lot_number) vers le product_id.
    if (data.mode === "BY_PRODUCT") {
      try {
        const lotsResp = await api.get(`/lots?distributor_id=${data.distributor_id}`);
        const m = new Map();
        for (const lot of lotsResp.data || []) {
          if (lot.lot_number && lot.product_id) {
            m.set(String(lot.lot_number).trim(), lot.product_id);
          }
        }
        setLotToProductMap(m);
      } catch {
        // Endpoint /lots peut ne pas exister sous cette forme — pas grave, fallback /scan/resolve
        setLotToProductMap(new Map());
      }
    }

    // Initialize drafts par clé unifiée (lot_id en BY_LOT, product_id en BY_PRODUCT)
    const d = {};
    const isProductMode = data.mode === "BY_PRODUCT";
    for (const it of data.items) {
      const key = isProductMode ? it.product_id : it.lot_id;
      if (key) {
        d[key] = it.counted_quantity !== null && it.counted_quantity !== undefined ? String(it.counted_quantity) : "";
      }
    }
    setDrafts(d);
  };
  useEffect(() => { load(); }, [id]);

  const productMap = useMemo(() => Object.fromEntries(products.map((p) => [p.id, p])), [products]);

  // Phase 91 — Map d'index local par lot_number, pour résolution INSTANTANÉE du scan
  // sans round-trip backend `/scan/resolve`. ~0.1ms au lieu de 500-1200ms.
  const lotIndex = useMemo(() => {
    const map = new Map();
    if (inv?.items) {
      for (const it of inv.items) {
        if (it.lot_number) map.set(String(it.lot_number).trim(), it);
      }
    }
    return map;
  }, [inv]);

  // Phase 91 — Helper : clé unifiée pour drafts / refs selon le mode
  const isProductMode = inv?.mode === "BY_PRODUCT";
  const itemKey = (it) => (isProductMode ? it.product_id : it.lot_id);

  // Phase 87aq — Tri des items :
  // - Mode BY_LOT : par emplacement croissant puis par lot_number (suivre l'ordre physique des cases)
  // - Mode BY_PRODUCT : par nom produit alphabétique (lecture humaine en frigo)
  const sortedItems = useMemo(() => {
    if (!inv?.items) return [];
    if (inv.mode === "BY_PRODUCT") {
      return [...inv.items].sort((a, b) => {
        const na = products.find((p) => p.id === a.product_id)?.name || "";
        const nb = products.find((p) => p.id === b.product_id)?.name || "";
        return na.localeCompare(nb, "fr", { sensitivity: "base" });
      });
    }
    return [...inv.items].sort((a, b) => {
      const ea = a.emplacement_number ?? 9999;
      const eb = b.emplacement_number ?? 9999;
      if (ea !== eb) return ea - eb;
      return String(a.lot_number).localeCompare(String(b.lot_number), "fr", { numeric: true });
    });
  }, [inv, products]);

  const saveItem = async (key) => {
    const val = drafts[key];
    if (val === "" || val === undefined) { toast.error("Saisissez une quantité"); return; }
    const n = Number(val);
    if (Number.isNaN(n) || n < 0) { toast.error("Quantité invalide"); return; }
    try {
      // Phase 91 — Payload différent selon le mode
      const body = isProductMode
        ? { product_id: key, counted_quantity: n }
        : { lot_id: key, counted_quantity: n };
      const { data } = await api.put(`/inventories/${id}/items`, body);
      setInv(data);
      toast.success("Comptage enregistré");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const saveAll = async () => {
    let saved = 0;
    for (const it of inv.items) {
      const key = itemKey(it);
      if (!key) continue;
      const val = drafts[key];
      if (val === "" || val === undefined) continue;
      const n = Number(val);
      if (Number.isNaN(n) || n < 0) continue;
      if (it.counted_quantity === n) continue;
      const body = isProductMode
        ? { product_id: it.product_id, counted_quantity: n }
        : { lot_id: it.lot_id, counted_quantity: n };
      await api.put(`/inventories/${id}/items`, body);
      saved++;
    }
    await load();
    toast.success(`${saved} lignes enregistrées`);
  };

  // Phase 87ag (17/06/2026) — Ajout d'un produit absent du tableau théorique
  // (cas typique d'un 1er inventaire d'un distributeur sans lots transférés).
  const openAdd = () => {
    setAddForm({ product_id: "", counted_quantity: "", lot_number: "", dlc: "", emplacement_number: "" });
    setAddOpen(true);
  };

  // Phase 87aq + 87bd — Handler scan UNIFIÉ. Utilise le parser backend `_parse_qr_payload`
  // via POST /api/scan/resolve, qui reconnaît :
  //   - URL publique de traçabilité (générée par les étiquettes) : `https://maison-thai.ch/trace/{sku}/{lot}`
  //   - JSON compact `{"l":"…","s":"…","d":"…"}`
  //   - Schéma propriétaire `maisonthai://lot/…`
  //   - Format compact `lot:…;sku:…;dlc:…`
  //   - Fallback : chaîne brute = lot_number
  // → Tous ces formats pointent vers UN lot précis (avec lot_id), donc on focus
  //   directement sur la BONNE ligne (pas le premier lot du produit au hasard).
  //
  // Pour les EAN13 bruts (12-13 chiffres) on garde la résolution locale via
  // `ean13_base`, mais avec désambiguïsation si plusieurs produits matchent.
  //
  // Phase 91 — Optimisation latence : avant /scan/resolve, on tente une résolution
  // 100% locale via `parseQrLocally` + `lotIndex` (Map en mémoire). Si le lot est
  // déjà dans l'inventaire courant, focus instantané (~0.1ms vs 500-1200ms réseau).
  const handleScan = async (rawCode) => {
    setScanOpen(false);
    let code = (rawCode || "").trim();
    if (!code) return;

    // Filet de sécurité scanner mal configuré (clavier US → AZERTY/CH-FR mojibake)
    const diag = unmojibakeWithDiagnostic(code);
    if (diag.wasCorrected) {
      code = diag.corrected;
      toast.warning(
        "Scanner en clavier US — payload corrigé auto. Reconfigurez le scanner en CH-FR (page Aide).",
        { duration: 4000 },
      );
    }

    // ── BRANCHE 0 (Phase 91) : RÉSOLUTION LOCALE ULTRA-RAPIDE ───────────
    // Si le QR/code peut être parsé localement → focus immédiat selon le mode.
    const parsedLocal = parseQrLocally(code);
    if (parsedLocal?.lot_number) {
      const lotNum = parsedLocal.lot_number.trim();

      if (isProductMode) {
        // Mode BY_PRODUCT : le QR contient un lot, on remonte au product_id via lotToProductMap
        const productId = lotToProductMap.get(lotNum);
        if (productId) {
          const item = inv.items.find((it) => it.product_id === productId);
          if (item) {
            focusItem(item);
            return;
          }
        }
        // Sinon fallback /scan/resolve (lot inconnu localement → backend connaît peut-être)
      } else {
        // Mode BY_LOT : match direct lot_number → item
        const item = lotIndex.get(lotNum);
        if (item) {
          focusItem(item);
          return;
        }
      }
    }

    // ── BRANCHE 1 : EAN13 pur (12-13 chiffres) ──────────────────────────
    // Résolution locale par `ean13_base`, avec désambiguïsation.
    if (/^\d{12,13}$/.test(code)) {
      const base = code.length === 13 ? code.slice(0, 12) : code;
      const matches = products.filter((p) => (p.ean13_base || "").trim() === base);
      if (matches.length === 0) {
        setScanError(`EAN « ${code} » : aucun produit ne porte ce code. Vérifiez la fiche produit (champ ean13_base).`);
        toast.error("EAN inconnu");
        return;
      }
      if (matches.length === 1) {
        // Phase 91 — En mode BY_PRODUCT, focus direct sur la ligne produit (1 ligne = 1 produit)
        if (isProductMode) {
          const item = inv.items.find((it) => it.product_id === matches[0].id);
          if (item) {
            focusItem(item, matches[0].name);
            return;
          }
          // Produit pas dans l'inventaire (cas rare en BY_PRODUCT) → ouvre add dialog
          toast.info(`${matches[0].name} reconnu — pas dans cet inventaire.`, { duration: 4000 });
          setAddForm({ product_id: matches[0].id, counted_quantity: "", lot_number: "", dlc: "", emplacement_number: "" });
          setAddOpen(true);
          return;
        }
        // Mode BY_LOT : comportement historique (focus 1er lot non compté)
        focusOnFirstLotForProduct(matches[0]);
        return;
      }
      // > 1 produit pour le même EAN → modal de désambiguïsation (plus de "premier au hasard")
      setAmbiguousCandidates(matches);
      setAmbiguousCode(code);
      setAmbiguousOpen(true);
      return;
    }

    // ── BRANCHE 2 : QR / lot_number / URL `/trace/…` ─────────────────────
    // Fallback backend si le lot n'est PAS dans l'inventaire courant
    // (= lot d'un autre dépôt, transféré entre-temps, etc.).
    setResolving(true);
    try {
      const { data } = await api.post("/scan/resolve", { payload: code });
      const lotId = data?.lot?.id;
      const productName = data?.product?.name || "Lot reconnu";

      // Le lot scanné fait-il partie de cet inventaire ?
      // Phase 91 — En mode BY_PRODUCT, on cherche par product_id (le lot agrégé)
      let itemInThisInventory;
      if (isProductMode) {
        const productId = data?.product?.id;
        itemInThisInventory = productId ? inv.items.find((it) => it.product_id === productId) : null;
      } else {
        itemInThisInventory = inv.items.find((it) => it.lot_id === lotId);
      }
      if (itemInThisInventory) {
        focusItem(itemInThisInventory, productName);
        return;
      }

      // Lot reconnu mais pas dans cet inventaire (lot d'un autre distributeur,
      // ou lot transféré entre temps). Propose d'ajouter au comptage.
      const matchedProductId = data?.product?.id;
      if (matchedProductId) {
        toast.info(
          `${productName} reconnu, mais pas dans cet inventaire. Pré-remplissage du dialog d'ajout.`,
          { duration: 5000 },
        );
        setAddForm({
          product_id: matchedProductId,
          counted_quantity: "",
          lot_number: data?.lot?.lot_number || "",
          dlc: data?.lot?.dlc ? data.lot.dlc.slice(0, 10) : "",
          emplacement_number: data?.lot?.emplacement_number ? String(data.lot.emplacement_number) : "",
        });
        setAddOpen(true);
        return;
      }

      setScanError(`Code « ${code} » résolu mais produit introuvable.`);
    } catch (err) {
      const detail = err?.response?.data?.detail || "Code QR illisible";
      setScanError(`${detail} — Si c'est une URL /trace/, vérifiez l'orthographe du SKU et du lot. Si c'est un scanner mal configuré, basculez-le en clavier CH-FR.`);
      toast.error(detail);
    } finally {
      setResolving(false);
    }
  };

  // Phase 91 — Focus instantané sur une ligne d'inventaire (scroll + select + toast).
  // Pas de setTimeout : l'input est déjà monté, on focus directement.
  const focusItem = (item, productName) => {
    setScanError(null);
    const key = isProductMode ? item.product_id : item.lot_id;
    const input = inputRefs.current[key];
    if (input) {
      input.scrollIntoView({ behavior: "smooth", block: "center" });
      input.focus();
      input.select();
    }
    const p = productMap[item.product_id];
    const displayName = productName || p?.name || "Lot reconnu";
    if (isProductMode) {
      toast.success(`${displayName} — total à compter`, { duration: 1500 });
    } else {
      const empLabel = item.emplacement_number ? ` (emp. ${item.emplacement_number})` : "";
      toast.success(`${displayName} — lot ${item.lot_number}${empLabel}`, { duration: 1800 });
    }
  };

  // Focus sur le 1er lot non compté d'un produit donné (fallback EAN13).
  const focusOnFirstLotForProduct = (product) => {
    const candidates = inv.items.filter((it) => it.product_id === product.id);
    if (candidates.length === 0) {
      toast.info(`${product.name} reconnu — pas dans cet inventaire. Pré-remplissage du dialog d'ajout.`, { duration: 6000 });
      setAddForm({ product_id: product.id, counted_quantity: "", lot_number: "", dlc: "", emplacement_number: "" });
      setAddOpen(true);
      return;
    }
    // Plusieurs lots du même produit → on doit choisir (pour HACCP, chaque lot a sa DLC).
    if (candidates.length > 1) {
      const notCounted = candidates.filter((it) => it.counted_quantity === null || it.counted_quantity === undefined);
      if (notCounted.length > 1) {
        // Désambiguïsation : modal liste des lots à choisir
        setAmbiguousCandidates(candidates.map((c) => ({
          isLotChoice: true,
          item: c,
          product,
        })));
        setAmbiguousCode(product.ean13_base || product.sku || "");
        setAmbiguousOpen(true);
        return;
      }
    }
    const target = candidates.find((it) => it.counted_quantity === null || it.counted_quantity === undefined) || candidates[0];
    focusItem(target, product.name);
  };

  // Phase 87bd — User a choisi un produit dans le modal de désambiguïsation EAN
  const pickAmbiguousProduct = (candidate) => {
    setAmbiguousOpen(false);
    setAmbiguousCandidates([]);
    if (candidate?.isLotChoice) {
      // Désambiguïsation lot : on focus directement sur la ligne choisie
      focusItem(candidate.item);
    } else {
      focusOnFirstLotForProduct(candidate);
    }
  };
  const submitAdd = async () => {
    if (!addForm.product_id) { toast.error("Choisis un produit"); return; }
    const n = Number(addForm.counted_quantity);
    if (Number.isNaN(n) || n < 0) { toast.error("Quantité invalide"); return; }
    setAddSaving(true);
    try {
      const body = {
        product_id: addForm.product_id,
        counted_quantity: n,
        lot_number: addForm.lot_number.trim() || null,
        dlc: addForm.dlc || null,
        emplacement_number: addForm.emplacement_number ? Number(addForm.emplacement_number) : null,
      };
      const { data } = await api.post(`/inventories/${id}/items/add`, body);
      setInv(data);
      const newItem = data.items[data.items.length - 1];
      if (newItem) {
        setDrafts((prev) => ({ ...prev, [newItem.lot_id]: String(n) }));
      }
      toast.success("Produit ajouté à l'inventaire");
      setAddOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setAddSaving(false);
    }
  };

  const closeInventory = async () => {
    const missing = inv.items.filter((it) => it.counted_quantity === null || it.counted_quantity === undefined);
    const ok = await confirm({
      title: "Clôturer l'inventaire ?",
      description: missing.length
        ? `${missing.length} ligne${missing.length > 1 ? "s" : ""} non comptée${missing.length > 1 ? "s" : ""} sera${missing.length > 1 ? "ont" : ""} ignorée${missing.length > 1 ? "s" : ""}. Les écarts seront appliqués en ajustements de stock.`
        : "Les écarts seront appliqués en ajustements de stock.",
      confirmText: "Clôturer",
    });
    if (!ok) return;
    try {
      const { data } = await api.post(`/inventories/${id}/close`);
      setInv(data);
      toast.success("Inventaire clôturé et écarts appliqués");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  if (!inv) return <div className="text-[#575A57]">Chargement…</div>;

  const totalItems = inv.items.length;
  const countedItems = inv.items.filter((it) => it.counted_quantity !== null && it.counted_quantity !== undefined).length;
  const discrepancies = inv.items.filter(
    (it) => it.counted_quantity !== null && it.counted_quantity !== undefined && it.counted_quantity !== it.theoretical_quantity
  );

  const isOpen = inv.status === "OPEN";

  return (
    <div className="space-y-6" data-testid="inventory-detail-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate("/inventaires")}>
            <ArrowLeft className="w-4 h-4 mr-1" /> Retour
          </Button>
          <div>
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#575A57]">Inventaire</div>
            <div className="font-heading text-xl font-medium">{inv.reference}</div>
          </div>
          {isOpen ? (
            <Badge variant="outline" className="bg-[#FBF4E9] border-[#E8D3A8] text-[#8C6B2F]">En cours</Badge>
          ) : (
            <Badge variant="outline" className="bg-[#EBF2E9] border-[#C7DDC0] text-[#2D6A4F]">Clôturé le {fmtDateTime(inv.closed_at)}</Badge>
          )}
          {/* Phase 91 — Badge mode (BY_PRODUCT = rapide, BY_LOT = audit HACCP) */}
          {isProductMode ? (
            <Badge variant="outline" className="bg-[#E8EEF6] border-[#A8BBD6] text-[#1F3A66]" data-testid="badge-mode-by-product">
              ⚡ Mode rapide (par produit)
            </Badge>
          ) : (
            <Badge variant="outline" className="bg-[#F1EAF8] border-[#C9B5E0] text-[#5B3A8C]" data-testid="badge-mode-by-lot">
              🔬 Mode détaillé (par lot HACCP)
            </Badge>
          )}
        </div>
        {isOpen && (
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" onClick={openAdd} data-testid="add-missing-item-btn" className="border-[#8C6B2F] text-[#8C6B2F] hover:bg-[#FBF4E9]">
              <Plus className="w-4 h-4 mr-2" /> Ajouter un produit manquant
            </Button>
            <Button variant="outline" onClick={saveAll} data-testid="save-all-btn">
              <Save className="w-4 h-4 mr-2" /> Enregistrer tout
            </Button>
            <Button
              onClick={closeInventory}
              className="bg-[#1F4E3D] hover:bg-[#173B2E]"
              disabled={countedItems === 0}
              data-testid="close-inventory-btn"
            >
              <CheckCircle2 className="w-4 h-4 mr-2" /> Clôturer & appliquer les écarts
            </Button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white border border-[#E5E5E0] rounded-lg p-5">
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#575A57]">Dépôt</div>
          <div className="font-heading text-lg font-medium mt-1.5">{distributor?.name || "—"}</div>
          <div className="text-xs font-mono text-[#575A57]">{distributor?.code || ""}</div>
        </div>
        <div className="bg-white border border-[#E5E5E0] rounded-lg p-5">
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#575A57]">Lots</div>
          <div className="font-heading text-3xl font-medium mt-1">{totalItems}</div>
          <div className="text-xs text-[#575A57]">à inventorier</div>
        </div>
        <div className="bg-white border border-[#E5E5E0] rounded-lg p-5">
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#575A57]">Progression</div>
          <div className="font-heading text-3xl font-medium mt-1">{countedItems} / {totalItems}</div>
          <div className="text-xs text-[#575A57]">{totalItems > 0 ? Math.round(100 * countedItems / totalItems) : 0}% comptés</div>
        </div>
        <div className={`border rounded-lg p-5 ${discrepancies.length > 0 ? "bg-[#FCEEEA] border-[#E8C1B4]" : "bg-white border-[#E5E5E0]"}`}>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#575A57]">Écarts détectés</div>
          <div className={`font-heading text-3xl font-medium mt-1 ${discrepancies.length > 0 ? "text-[#C05C47]" : ""}`}>{discrepancies.length}</div>
          <div className="text-xs text-[#575A57]">{discrepancies.length > 0 ? "à valider à la clôture" : "aucun écart"}</div>
        </div>
      </div>

      {/* Phase 87ar — Champ scan HID Bluetooth/USB (Inateck BCST-47 & co)
          Phase 87bd — Délégation au backend /scan/resolve pour parser QR + URL trace */}
      {isOpen && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            const raw = hidScanInput;
            setHidScanInput("");
            handleScan(raw);
          }}
          className="bg-[#1F4E3D]/5 border-2 border-[#1F4E3D]/30 rounded-lg p-3 flex items-center gap-3"
          data-testid="hid-scan-form"
        >
          <div className="flex items-center gap-1.5 text-[#1F4E3D] shrink-0">
            {resolving ? (
              <div className="w-4 h-4 border-2 border-[#1F4E3D]/30 border-t-[#1F4E3D] rounded-full animate-spin" />
            ) : (
              <ScanLine className="w-4 h-4" />
            )}
            <span className="text-xs font-semibold uppercase tracking-wider">
              {resolving ? "Résolution…" : "Lecteur scan"}
            </span>
          </div>
          <Input
            ref={hidScanFieldRef}
            value={hidScanInput}
            onChange={(e) => setHidScanInput(e.target.value)}
            placeholder="Scannez QR étiquette (URL /trace/…) OU EAN13 (12-13 chiffres) — Inateck USB/2.4GHz OK"
            className="font-mono bg-white border-[#1F4E3D]/40 focus:border-[#1F4E3D]"
            autoFocus
            disabled={resolving}
            data-testid="hid-scan-input"
          />
          <Button type="button" variant="outline" onClick={() => setScanOpen(true)} size="sm" data-testid="open-camera-btn" title="Utiliser la caméra à la place">
            <Camera className="w-4 h-4 mr-1" /> Caméra
          </Button>
        </form>
      )}

      <div className="bg-white border border-[#E5E5E0] rounded-lg overflow-hidden">
        {isProductMode ? (
          /* Phase 91 — Vue BY_PRODUCT : 1 ligne par produit (pas par lot).
             Simplifiée pour comptage rapide centrale. */
          <Table data-testid="inventory-by-product-table">
            <TableHeader>
              <TableRow className="bg-[#F4F4F0] hover:bg-[#F4F4F0]">
                <TableHead className="text-xs uppercase tracking-wider font-semibold text-[#575A57]">Produit</TableHead>
                <TableHead className="text-xs uppercase tracking-wider font-semibold text-[#575A57] text-right w-32">Théorique (somme lots)</TableHead>
                <TableHead className="text-xs uppercase tracking-wider font-semibold text-[#575A57] text-right w-32">Compté total</TableHead>
                <TableHead className="text-xs uppercase tracking-wider font-semibold text-[#575A57] text-right w-24">Écart</TableHead>
                <TableHead className="text-xs uppercase tracking-wider font-semibold text-[#575A57]">Horodatage</TableHead>
                <TableHead className="text-xs uppercase tracking-wider font-semibold text-[#575A57] text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody data-testid="inventory-items-body">
              {inv.items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-[#575A57] py-10">
                    Aucun produit avec stock dans ce dépôt.
                  </TableCell>
                </TableRow>
              ) : sortedItems.map((it) => {
                const p = productMap[it.product_id];
                const counted = it.counted_quantity;
                const diff = counted !== null && counted !== undefined ? counted - it.theoretical_quantity : null;
                const draftKey = it.product_id;
                return (
                  <TableRow key={it.product_id} data-testid={`inv-product-${it.product_id}`} className="hover:bg-[#F9F9F6]">
                    <TableCell className="font-medium py-3">
                      {p?.name || "—"} <span className="text-xs text-[#575A57] font-mono ml-1">({p?.sku})</span>
                    </TableCell>
                    <TableCell className="text-right font-mono text-base">{fmtNum(it.theoretical_quantity, p?.unit)}</TableCell>
                    <TableCell className="text-right">
                      {isOpen ? (
                        <Input
                          ref={(el) => { if (el) inputRefs.current[draftKey] = el; }}
                          type="number"
                          inputMode="decimal"
                          className="w-28 ml-auto text-right font-mono text-base h-11"
                          value={drafts[draftKey] ?? ""}
                          onChange={(e) => setDrafts({ ...drafts, [draftKey]: e.target.value })}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              e.target.blur();
                            }
                          }}
                          onBlur={() => {
                            const val = drafts[draftKey];
                            if (val === "" || val === undefined) return;
                            if (Number(val) !== counted) saveItem(draftKey);
                          }}
                          data-testid={`count-input-product-${it.product_id}`}
                        />
                      ) : (
                        <span className="font-mono text-base">{counted !== null && counted !== undefined ? fmtNum(counted, p?.unit) : "—"}</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {diff === null ? (
                        <span className="text-[#A3A6A3]">—</span>
                      ) : diff === 0 ? (
                        <Badge variant="outline" className="bg-[#EBF2E9] border-[#C7DDC0] text-[#2D6A4F]">OK</Badge>
                      ) : (
                        <span className={diff > 0 ? "text-[#2D6A4F] font-semibold" : "text-[#C05C47] font-semibold"}>
                          {diff > 0 ? "+" : ""}{fmtNum(diff, p?.unit)}
                          {diff < 0 && <AlertTriangle className="w-3 h-3 inline ml-1" />}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-[#575A57] font-mono">{fmtDateTime(it.counted_at)}</TableCell>
                    <TableCell className="text-right">
                      {isOpen && drafts[draftKey] !== "" && drafts[draftKey] !== undefined && Number(drafts[draftKey]) !== counted && (
                        <Button variant="ghost" size="sm" onClick={() => saveItem(draftKey)} data-testid={`save-count-product-${it.product_id}`}>
                          <Save className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        ) : (
          /* Mode BY_LOT (historique) : 1 ligne par lot avec emplacement, lot_number, DLC */
          <Table data-testid="inventory-by-lot-table">
            <TableHeader>
              <TableRow className="bg-[#F4F4F0] hover:bg-[#F4F4F0]">
                <TableHead className="text-xs uppercase tracking-wider font-semibold text-[#575A57] w-20">
                  <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />Empl.</span>
                </TableHead>
                <TableHead className="text-xs uppercase tracking-wider font-semibold text-[#575A57]">N° de lot</TableHead>
                <TableHead className="text-xs uppercase tracking-wider font-semibold text-[#575A57]">Produit</TableHead>
                <TableHead className="text-xs uppercase tracking-wider font-semibold text-[#575A57] text-right">Théorique</TableHead>
                <TableHead className="text-xs uppercase tracking-wider font-semibold text-[#575A57] text-right">Compté</TableHead>
                <TableHead className="text-xs uppercase tracking-wider font-semibold text-[#575A57] text-right">Écart</TableHead>
                <TableHead className="text-xs uppercase tracking-wider font-semibold text-[#575A57]">Horodatage</TableHead>
                <TableHead className="text-xs uppercase tracking-wider font-semibold text-[#575A57] text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody data-testid="inventory-items-body">
              {inv.items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center text-[#575A57] py-10">
                    Aucun lot avec stock dans ce dépôt au moment de l&apos;inventaire.
                  </TableCell>
                </TableRow>
              ) : sortedItems.map((it) => {
                const p = productMap[it.product_id];
                const counted = it.counted_quantity;
                const diff = counted !== null && counted !== undefined ? counted - it.theoretical_quantity : null;
                return (
                  <TableRow key={it.lot_id} data-testid={`inv-item-${it.lot_number}`}>
                    <TableCell className="font-mono text-center">
                      {it.emplacement_number ? (
                        <Badge variant="outline" className="bg-[#FBF4E9] border-[#C1A063] text-[#8C6B2F] font-mono text-xs">
                          {it.emplacement_number}
                        </Badge>
                      ) : (
                        <span className="text-[#A3A6A3] text-xs">—</span>
                      )}
                    </TableCell>
                    <TableCell className="font-mono">{it.lot_number}</TableCell>
                    <TableCell className="font-medium">{p?.name || "—"} <span className="text-xs text-[#575A57] font-mono">({p?.sku})</span></TableCell>
                    <TableCell className="text-right font-mono">{fmtNum(it.theoretical_quantity, p?.unit)}</TableCell>
                    <TableCell className="text-right">
                      {isOpen ? (
                        <Input
                          ref={(el) => { if (el) inputRefs.current[it.lot_id] = el; }}
                          type="number"
                          className="w-28 ml-auto text-right font-mono"
                          value={drafts[it.lot_id] ?? ""}
                          onChange={(e) => setDrafts({ ...drafts, [it.lot_id]: e.target.value })}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              e.target.blur();
                            }
                          }}
                          onBlur={() => {
                            const val = drafts[it.lot_id];
                            if (val === "" || val === undefined) return;
                            if (Number(val) !== counted) saveItem(it.lot_id);
                          }}
                          data-testid={`count-input-${it.lot_number}`}
                        />
                      ) : (
                        <span className="font-mono">{counted !== null && counted !== undefined ? fmtNum(counted, p?.unit) : "—"}</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {diff === null ? (
                        <span className="text-[#A3A6A3]">—</span>
                      ) : diff === 0 ? (
                        <Badge variant="outline" className="bg-[#EBF2E9] border-[#C7DDC0] text-[#2D6A4F]">OK</Badge>
                      ) : (
                        <span className={diff > 0 ? "text-[#2D6A4F] font-semibold" : "text-[#C05C47] font-semibold"}>
                          {diff > 0 ? "+" : ""}{fmtNum(diff, p?.unit)}
                          {diff < 0 && <AlertTriangle className="w-3 h-3 inline ml-1" />}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-[#575A57] font-mono">{fmtDateTime(it.counted_at)}</TableCell>
                    <TableCell className="text-right">
                      {isOpen && drafts[it.lot_id] !== "" && drafts[it.lot_id] !== undefined && Number(drafts[it.lot_id]) !== counted && (
                        <Button variant="ghost" size="sm" onClick={() => saveItem(it.lot_id)} data-testid={`save-count-${it.lot_number}`}>
                          <Save className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </div>

      {inv.notes && (
        <div className="bg-[#F9F9F6] border border-[#E5E5E0] rounded-lg p-4 text-sm text-[#575A57]">
          <div className="text-xs uppercase tracking-[0.2em] font-semibold mb-1.5">Notes</div>
          {inv.notes}
        </div>
      )}

      {/* Phase 87aq — Bandeau d'erreur scan (clavier ou caméra) */}
      {scanError && (
        <div className="bg-[#FCEEEA] border border-[#E8C1B4] rounded-lg p-3 flex items-start gap-2 text-sm text-[#8B1A1A]" data-testid="scan-error-banner">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <div className="flex-1">{scanError}</div>
          <button onClick={() => setScanError(null)} className="text-[#8B1A1A] hover:underline text-xs">Fermer</button>
        </div>
      )}

      {/* Phase 87aq — Scanner caméra (EAN13 + QR lot) */}
      <CameraScanner open={scanOpen} onClose={() => setScanOpen(false)} onDetected={handleScan} />

      {/* Phase 87bd — Dialog désambiguïsation EAN13 / lot multiple
          Quand un EAN matche plusieurs produits OU un produit a plusieurs lots
          non comptés, l'utilisateur DOIT choisir (HACCP : chaque lot a sa DLC). */}
      <Dialog open={ambiguousOpen} onOpenChange={setAmbiguousOpen}>
        <DialogContent className="max-w-lg" data-testid="ambiguous-scan-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-[#8C6B2F]" />
              Choix nécessaire — {ambiguousCandidates[0]?.isLotChoice ? "plusieurs lots" : "plusieurs produits partagent cet EAN"}
            </DialogTitle>
            <DialogDescription>
              {ambiguousCandidates[0]?.isLotChoice
                ? "Plusieurs lots de ce produit sont présents dans l'inventaire (DLC différentes). Sélectionnez celui que vous comptez actuellement."
                : <>L&apos;EAN13 <code className="font-mono bg-[#F4F4F0] px-1.5 py-0.5 rounded text-xs">{ambiguousCode}</code> est attribué à plusieurs produits. Sélectionnez celui que vous scannez.</>
              }
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2 max-h-[60vh] overflow-y-auto">
            {ambiguousCandidates.map((cand, idx) => {
              if (cand.isLotChoice) {
                const it = cand.item;
                const p = cand.product;
                const dlcStr = it.dlc ? new Date(it.dlc).toLocaleDateString("fr-CH") : "—";
                const alreadyCounted = it.counted_quantity !== null && it.counted_quantity !== undefined;
                return (
                  <button
                    key={it.lot_id}
                    onClick={() => pickAmbiguousProduct(cand)}
                    className="w-full text-left p-3 border border-[#E5E5E0] rounded-lg hover:border-[#1F4E3D] hover:bg-[#F4F8F5] transition-colors flex items-center justify-between gap-3"
                    data-testid={`ambiguous-lot-${it.lot_number}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="font-medium">{p?.name || "—"}</div>
                      <div className="text-xs text-[#575A57] font-mono mt-0.5">
                        Lot {it.lot_number} · DLC {dlcStr}
                        {it.emplacement_number && ` · Empl. ${it.emplacement_number}`}
                      </div>
                    </div>
                    <div className="text-right">
                      {alreadyCounted ? (
                        <Badge variant="outline" className="bg-[#EBF2E9] border-[#C7DDC0] text-[#2D6A4F]">
                          Compté ({it.counted_quantity})
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="bg-[#FBF4E9] border-[#E8D3A8] text-[#8C6B2F]">
                          À compter
                        </Badge>
                      )}
                    </div>
                  </button>
                );
              }
              // Cas EAN13 → choix entre plusieurs PRODUITS
              return (
                <button
                  key={cand.id || idx}
                  onClick={() => pickAmbiguousProduct(cand)}
                  className="w-full text-left p-3 border border-[#E5E5E0] rounded-lg hover:border-[#1F4E3D] hover:bg-[#F4F8F5] transition-colors"
                  data-testid={`ambiguous-product-${cand.sku}`}
                >
                  <div className="font-medium">{cand.name}</div>
                  <div className="text-xs text-[#575A57] font-mono mt-0.5">
                    SKU {cand.sku} · catégorie {cand.category || "—"}
                  </div>
                </button>
              );
            })}
            {ambiguousCandidates.length === 0 && (
              <p className="text-sm text-[#575A57] italic text-center py-4">Aucun candidat.</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAmbiguousOpen(false)}>Annuler</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Phase 87ag — Dialog Ajout produit manquant */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-md" data-testid="add-missing-item-dialog">
          <DialogHeader>
            <DialogTitle>Ajouter un produit manquant</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-xs text-[#575A57]">
              Crée un nouveau lot au distributeur sélectionné avec la quantité comptée. La DLC sera calculée auto si non renseignée (production = aujourd&apos;hui + shelf_life du produit).
            </p>
            <div>
              <Label className="mb-1.5 block">Produit *</Label>
              <Select value={addForm.product_id} onValueChange={(v) => setAddForm((f) => ({ ...f, product_id: v }))}>
                <SelectTrigger data-testid="add-product-select"><SelectValue placeholder="Sélectionner un produit…" /></SelectTrigger>
                <SelectContent>
                  {products.slice().sort((a, b) => (a.name || "").localeCompare(b.name || "")).map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.sku} · {p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="mb-1.5 block">Quantité comptée *</Label>
              <Input
                type="number"
                step="1"
                min="0"
                value={addForm.counted_quantity}
                onChange={(e) => setAddForm((f) => ({ ...f, counted_quantity: e.target.value }))}
                data-testid="add-qty-input"
                placeholder="ex: 8"
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="mb-1.5 block">N° lot</Label>
                <Input
                  value={addForm.lot_number}
                  onChange={(e) => setAddForm((f) => ({ ...f, lot_number: e.target.value }))}
                  placeholder="auto si vide"
                  data-testid="add-lot-input"
                />
              </div>
              <div>
                <Label className="mb-1.5 block">DLC</Label>
                <Input
                  type="date"
                  value={addForm.dlc}
                  onChange={(e) => setAddForm((f) => ({ ...f, dlc: e.target.value }))}
                  data-testid="add-dlc-input"
                />
              </div>
            </div>
            {/* Phase 87aq — Emplacement physique */}
            <div>
              <Label className="mb-1.5 block flex items-center gap-1">
                <MapPin className="w-3 h-3" /> Emplacement physique
              </Label>
              <Input
                type="number"
                min="0"
                value={addForm.emplacement_number}
                onChange={(e) => setAddForm((f) => ({ ...f, emplacement_number: e.target.value }))}
                placeholder="ex: 12 (n° de case / rail)"
                data-testid="add-emplacement-input"
              />
              <p className="text-[10px] text-[#A3A6A3] mt-1">
                N° de la case ou du rail dans le distributeur (facultatif, accélère l&apos;inventaire suivant).
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)} disabled={addSaving}>Annuler</Button>
            <Button onClick={submitAdd} disabled={addSaving} className="bg-[#1F4E3D] hover:bg-[#173B2E]" data-testid="add-submit-btn">
              {addSaving ? "Enregistrement…" : "Ajouter"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
